/* ONE TUNNE cart: <cart-drawer>, <cart-items>. Cart AJAX + Section Rendering
 * API; strings come from data-* markup, never this file. Document events: cart:updated,
 * cart:error, cart:add; drawer API: open/close/addItems(items, trigger); opt-in submit
 * interception via form[data-cart-add]. No framework, no storage, no trackers. */
(() => {
  if (customElements.get('cart-drawer')) return;

  const ITEMS = '[data-cart-items]';
  const SUMMARY = '[data-cart-summary]';

  const root = () => {
    const routes = window.Shopify && window.Shopify.routes;
    return (routes && routes.root) || '/';
  };

  const drawerEl = () => document.querySelector('cart-drawer');

  const sectionIds = () => {
    const ids = [];
    const drawer = drawerEl();
    if (drawer && drawer.dataset.sectionId) ids.push(drawer.dataset.sectionId);
    document.querySelectorAll('[data-cart-section-id]').forEach((node) => {
      const id = node.dataset.cartSectionId;
      if (id && !ids.includes(id)) ids.push(id);
    });
    return ids.slice(0, 5);
  };

  const errorBox = (trigger) => {
    const scope = (trigger && trigger.closest('[data-cart-error-scope]')) || document;
    return scope.querySelector('[data-cart-error]');
  };

  const showError = (trigger, message) => {
    const box = errorBox(trigger);
    const text = message || (box && box.dataset.errorGeneric) || '';
    if (box) {
      box.textContent = text;
      box.hidden = false;
    }
    document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: text } }));
  };

  const clearError = (trigger) => {
    const box = errorBox(trigger);
    if (box) {
      box.textContent = '';
      box.hidden = true;
    }
  };

  /* Spinner (.btn--loading) + aria-busy while a request flies. */
  const setBusy = (trigger, on, keepEnabled) => {
    let node = trigger;
    if (node && node.tagName === 'FORM') node = node.querySelector('[type="submit"], button');
    if (!node || !node.classList) return;
    if (node.tagName !== 'BUTTON' && !node.classList.contains('btn')) return;
    node.classList.toggle('btn--loading', !!on);
    if (on) node.setAttribute('aria-busy', 'true');
    else node.removeAttribute('aria-busy');
    if (!keepEnabled && 'disabled' in node) node.disabled = !!on;
  };

  const applySections = (sections) => {
    if (!sections) return;
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (!html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const drawer = drawerEl();
      const live =
        drawer && drawer.dataset.sectionId === id
          ? drawer
          : document.getElementById(`shopify-section-${id}`);
      if (!live) return;
      let swapped = false;
      [ITEMS, SUMMARY].forEach((selector) => {
        const next = parsed.querySelector(selector);
        const current = live.querySelector(selector);
        if (!next || !current) return;
        current.innerHTML = next.innerHTML;
        if (next.hasAttribute('data-count')) current.dataset.count = next.dataset.count;
        swapped = true;
      });
      if (swapped) return;
      const wrapper = parsed.getElementById(`shopify-section-${id}`);
      if (wrapper) live.innerHTML = wrapper.innerHTML;
    });
  };

  const countFromDom = () => {
    const source = document.querySelector('[data-cart-count-source]');
    if (!source) return null;
    const value = Number(source.dataset.count);
    return Number.isFinite(value) ? value : null;
  };

  const post = async (endpoint, payload, trigger) => {
    const ids = sectionIds().join(',');
    const init = { method: 'POST', headers: { Accept: 'application/json' } };
    if (payload instanceof FormData) {
      payload.delete('sections');
      payload.delete('sections_url');
      if (ids) payload.append('sections', ids);
      payload.append('sections_url', window.location.pathname);
      init.body = payload;
    } else {
      if (ids) payload.sections = ids;
      payload.sections_url = window.location.pathname;
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(payload);
    }

    let data = {};
    let ok = false;
    setBusy(trigger, true);
    try {
      const response = await fetch(root() + endpoint, init);
      ok = response.ok;
      data = await response.json();
    } catch (error) {
      ok = false;
      data = {};
    } finally {
      setBusy(trigger, false);
    }

    if (!ok) {
      showError(trigger, data.description || data.message);
      return null;
    }

    clearError(trigger);
    applySections(data.sections);
    const counted = countFromDom();
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart: data, itemCount: counted === null ? data.item_count : counted }
      })
    );
    return data;
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.opener = null;
      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-cart-drawer-close]')) return;
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => {
        const main = this.mainEl();
        if (main) main.inert = false;
        if (this.opener && this.opener.isConnected) this.opener.focus();
        this.opener = null;
      });
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    onDocumentClick(event) {
      if (this.dataset.cartType !== 'drawer') return;
      if (event.defaultPrevented || event.button) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest('[data-cart-drawer-open], a[href]');
      if (!trigger || this.contains(trigger)) return;
      if (!trigger.matches('[data-cart-drawer-open]')) {
        if (trigger.hasAttribute('data-no-drawer') || trigger.target === '_blank') return;
        const href = (trigger.getAttribute('href') || '').split('?')[0].split('#')[0];
        const cartUrl = this.dataset.cartUrl || '';
        if (href.replace(/\/$/, '') !== cartUrl.replace(/\/$/, '')) return;
      }
      event.preventDefault();
      this.open(trigger);
    }

    mainEl() {
      const main = document.getElementById('main-content');
      return main && !main.contains(this) ? main : null;
    }

    open(opener) {
      if (!this.dialog) return;
      this.opener = opener || document.activeElement;
      const main = this.mainEl();
      if (main) main.inert = true;
      if (!this.dialog.open) this.dialog.showModal();
      const close = this.dialog.querySelector('[data-cart-drawer-close]');
      if (close) close.focus();
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    async addItems(items, trigger) {
      if (!items) return null;
      const data = await post('cart/add.js', { items }, trigger);
      if (data) this.open(trigger);
      return data;
    }

    async addForm(form, trigger) {
      const data = await post('cart/add.js', new FormData(form), trigger);
      if (data) this.open(trigger);
      return data;
    }
  }

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this);
      this.addEventListener('click', this);
    }

    handleEvent(event) {
      if (event.type === 'change') {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input) return;
        // An emptied field is not "0": restore it instead of removing the line.
        if (input.value.trim() === '') { input.value = input.defaultValue; return; }
        this.change(input, Math.max(0, parseInt(input.value, 10) || 0));
        return;
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      this.change(remove, 0);
    }

    async change(node, quantity) {
      const line = Number(node.dataset.line);
      if (!Number.isFinite(line) || line < 1) return;
      this.setAttribute('aria-busy', 'true');
      const data = await post('cart/change.js', { line, quantity }, node);
      this.setAttribute('aria-busy', 'false');
      if (!data && node.defaultValue !== undefined) node.value = node.defaultValue;
    }
  }

  // <cart-count> lives in assets/header.js (the header loads first and owns the element).

  document.addEventListener('cart:add', async (event) => {
    const drawer = drawerEl();
    const detail = event.detail || {};
    if (drawer && drawer.dataset.cartType === 'drawer') {
      drawer.addItems(detail.items, detail.trigger);
      return;
    }
    // Cart type "page": add, then go to the cart page instead of opening a drawer.
    const data = await post('cart/add.js', { items: detail.items }, detail.trigger);
    const routes = window.Shopify && window.Shopify.routes;
    if (data) window.location.href = (routes && routes.cart_url) || root() + 'cart';
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches || !form.matches('form[data-cart-add]')) return;
    const drawer = drawerEl();
    if (!drawer || drawer.dataset.cartType !== 'drawer') {
      // Native submit will navigate: spinner on, but keep the submitter enabled.
      setBusy(event.submitter || form, true, true);
      return;
    }
    event.preventDefault();
    drawer.addForm(form, event.submitter || form);
  });

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };

  define('cart-items', CartItems);
  define('cart-drawer', CartDrawer);
})();
